// Mantine SegmentedControl's parity on Lumen tokens: one pressed button out of a few, in a segmented frame. Toggle
// buttons (`aria-pressed`) in a named group, like the patch-log locale switcher; not Tabs (a value pick has no panels)
// and not a RadioGroup (the radio look is wrong for a segmented strip). Lifted from role-form-modal's
// `SegmentedToggle` (revamp PR 4) for PR 7c, same markup and classes.
//
//   <SegmentedControl
//     value={state}                                    // the pressed segment
//     options={[{ value: 'DRAFT', label: 'DRAFT' }, { value: 'FINAL', label: 'FINAL' }]}
//     onChange={setState}                              // only on a different segment (Mantine's rule)
//     fullWidth                                        // the strip fills its row, segments share it
//     aria-label={t('…')}                              // or aria-labelledby: the group's name
//   />
//
// Clicking the pressed segment does not call `onChange`. `className` goes on the group.
import { cn } from '@/lib/utils';

export interface SegmentedControlOption<V extends string> {
  value: V;
  label: string;
}

export interface SegmentedControlProps<V extends string> {
  value: V;
  options: SegmentedControlOption<V>[];
  /** The picked segment's value; never called for the segment already pressed. */
  onChange: (value: V) => void;
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export function SegmentedControl<V extends string>({
  value,
  options,
  onChange,
  fullWidth,
  className,
  ...labelling
}: SegmentedControlProps<V>) {
  return (
    <div
      role="group"
      {...labelling}
      className={cn(
        'inline-flex shrink-0 gap-0.5 rounded-card bg-surface-2 p-[3px]',
        fullWidth && 'flex w-full',
        className
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            className={cn(
              'inline-flex h-7 items-center justify-center whitespace-nowrap rounded-control border px-2.5 font-medium text-xs outline-none transition-[color,background-color,border-color] focus-visible:ring-[3px] focus-visible:ring-label/40',
              fullWidth && 'flex-1',
              active
                ? 'border-line-strong bg-raised font-semibold text-fg'
                : 'border-transparent text-fg-muted hover:text-fg'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
